import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { documentsService } from '../services';
import { useTranslation } from '../i18n';
import {
  ArrowLeft, CheckCircle, Circle, FileText, Save, Loader2, AlertCircle, ChevronRight,
} from 'lucide-react';

const DOC_ICONS = {
  about_my_child: '👶',
  love_letter:    '💌',
  medical_info:   '🏥',
  emergency:      '🆘',
  consent:        '📋',
  default:        '📄',
};

function DocSidebar({ docs, activeId, onSelect, t }) {
  return (
    <aside className="w-full lg:w-72 flex-shrink-0">
      <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Documents</p>
        </div>
        <div className="divide-y divide-slate-50">
          {docs.map(doc => {
            const isActive = doc.id === activeId;
            const icon = DOC_ICONS[doc.document_type] || DOC_ICONS.default;
            return (
              <button
                key={doc.id}
                data-testid={`doc-tab-${doc.document_type}`}
                onClick={() => onSelect(doc.id)}
                className={`w-full text-left px-5 py-4 flex items-center gap-3 transition-all duration-150
                  ${isActive ? 'bg-sky-50 border-r-2 border-sky-500' : 'hover:bg-slate-50'}`}
              >
                <span className="text-xl flex-shrink-0">{icon}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${isActive ? 'text-sky-700' : 'text-slate-800'}`}>
                    {doc.title}
                  </p>
                  {doc.required && (
                    <span className="text-xs text-amber-600 font-medium">{t('documents.required')}</span>
                  )}
                </div>
                {doc.is_complete
                  ? <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                  : <Circle size={16} className="text-slate-300 flex-shrink-0" />
                }
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

function DocEditor({ doc, onSave, t }) {
  const [content, setContent]         = useState(doc.content || '');
  const [isComplete, setIsComplete]   = useState(doc.is_complete || false);
  const [saving, setSaving]           = useState(false);
  const [savedMsg, setSavedMsg]       = useState('');
  const icon = DOC_ICONS[doc.document_type] || DOC_ICONS.default;

  // Sync when doc changes
  useEffect(() => {
    setContent(doc.content || '');
    setIsComplete(doc.is_complete || false);
    setSavedMsg('');
  }, [doc.id]);

  const handleSave = async (complete = isComplete) => {
    setSaving(true);
    try {
      await onSave({ document_type: doc.document_type, updated_content: content, is_complete: complete });
      setIsComplete(complete);
      setSavedMsg(t('documents.saved'));
      setTimeout(() => setSavedMsg(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const wordCount = content.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="flex-1 flex flex-col gap-4">
      {/* Doc header */}
      <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm px-7 py-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0">
            {icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-slate-900 font-outfit">{doc.title}</h2>
              {doc.required && (
                <span className="text-xs bg-amber-100 text-amber-700 font-semibold px-2.5 py-1 rounded-full">
                  {t('documents.required')}
                </span>
              )}
              {isComplete && (
                <span className="text-xs bg-green-100 text-green-700 font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                  <CheckCircle size={11} /> {t('common.complete')}
                </span>
              )}
            </div>
            {doc.description && (
              <p className="text-slate-500 text-sm mt-1 leading-relaxed">{doc.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Textarea */}
      <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm flex-1 flex flex-col overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">{t('documents.editor')}</p>
          <span className="text-xs text-slate-400">{wordCount} words</span>
        </div>
        <textarea
          data-testid="doc-editor-textarea"
          className="flex-1 w-full px-6 py-5 text-slate-800 text-sm leading-relaxed resize-none outline-none font-nunito min-h-[280px]"
          placeholder={`Write your ${doc.title.toLowerCase()} here...`}
          value={content}
          onChange={e => setContent(e.target.value)}
        />
      </div>

      {/* Action bar */}
      <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <button
          data-testid="doc-toggle-complete"
          onClick={() => handleSave(!isComplete)}
          disabled={saving}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold transition-all border
            ${isComplete
              ? 'border-slate-200 text-slate-600 hover:bg-slate-50'
              : 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'}`}
        >
          {isComplete ? <Circle size={15} /> : <CheckCircle size={15} />}
          {isComplete ? t('documents.markIncomplete') : t('documents.markComplete')}
        </button>

        <div className="flex items-center gap-3">
          {savedMsg && (
            <span className="text-green-600 text-sm font-semibold flex items-center gap-1">
              <CheckCircle size={14} /> {savedMsg}
            </span>
          )}
          <button
            data-testid="doc-save-btn"
            onClick={() => handleSave()}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white rounded-2xl text-sm font-semibold transition-all shadow-sm"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? t('documents.saving') : t('documents.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Documents() {
  const { campId }  = useParams();
  const location    = useLocation();
  const navigate    = useNavigate();
  const { t }       = useTranslation();
  const participantId = new URLSearchParams(location.search).get('participant_id');

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [activeDocId, setActiveDocId] = useState(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await documentsService.getDocuments(campId, participantId);
      setData(res);
      if (res.documents?.length) setActiveDocId(res.documents[0].id);
    } catch {
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [campId, participantId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleSave = async (payload) => {
    await documentsService.updateDocument(campId, { recipient_id: participantId, ...payload });
    // Update local state
    setData(prev => ({
      ...prev,
      documents: prev.documents.map(d =>
        d.document_type === payload.document_type
          ? { ...d, content: payload.updated_content, is_complete: payload.is_complete }
          : d
      ),
    }));
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 border border-red-200 text-center max-w-sm shadow-sm">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-slate-700 font-medium mb-4">{error}</p>
        <button onClick={fetchDocs} className="px-6 py-2.5 bg-sky-500 text-white rounded-full text-sm font-semibold hover:bg-sky-600">
          {t('common.retry')}
        </button>
      </div>
    </div>
  );

  const docs = data?.documents || [];
  const completedCount = docs.filter(d => d.is_complete).length;
  const activeDoc = docs.find(d => d.id === activeDocId);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Breadcrumb */}
        <button
          data-testid="docs-back-btn"
          onClick={() => navigate('/my-camps')}
          className="flex items-center gap-1.5 text-sky-500 text-sm font-semibold hover:text-sky-700 transition-colors mb-6"
        >
          <ArrowLeft size={15} /> {t('documents.back')}
        </button>

        {/* Header */}
        <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm px-7 py-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-sky-500 text-xs font-bold uppercase tracking-[0.2em] mb-1 flex items-center gap-2">
                <span className="w-5 h-px bg-sky-400 inline-block" />
                {t('documents.section')}
              </p>
              <h1 className="text-2xl font-bold text-slate-900 font-outfit">
                {data?.camp_name || 'Camp Documents'}
              </h1>
              {data?.participant_name && (
                <p className="text-slate-500 text-sm mt-0.5">For: <span className="font-semibold text-slate-700">{data.participant_name}</span></p>
              )}
            </div>

            {/* Progress pill */}
            <div className="flex-shrink-0">
              {completedCount === docs.length && docs.length > 0 ? (
                <div className="flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
                  <CheckCircle size={16} /> {t('documents.allDone')}
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-sky-50 text-sky-700 px-4 py-2 rounded-full text-sm font-semibold">
                  <FileText size={16} /> {completedCount}/{docs.length} {t('documents.progress')}
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {docs.length > 0 && (
            <div className="mt-4">
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className="bg-sky-500 h-2 rounded-full transition-all duration-700"
                  style={{ width: `${docs.length ? (completedCount / docs.length) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {docs.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200/60 p-12 text-center shadow-sm">
            <FileText size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">{t('documents.noDocuments')}</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            <DocSidebar docs={docs} activeId={activeDocId} onSelect={setActiveDocId} t={t} />
            {activeDoc && <DocEditor doc={activeDoc} onSave={handleSave} t={t} />}
          </div>
        )}
      </div>
    </div>
  );
}
